import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Grid,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Paper,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Delete, ArrowBack, Dashboard, QrCode2, Logout } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import BrandedQRCode from './BrandedQRCode.jsx';

const FormPage = () => {
  const userEmail = JSON.parse(sessionStorage.getItem('user'))?.email || "";
  const isAdmin = JSON.parse(sessionStorage.getItem('user'))?.isAdmin || false;
  const [formData, setFormData] = useState({
    deliveryDate: null,
    packageNumber: "",
    vendorName: "",
    location: "",
    owner: "",
    createdBy: userEmail,
    newItem: "",
    items: [],
  });
  const [qrData, setQrData] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState({ open: false, message: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const { deliveryDate, packageNumber, vendorName, location, owner, createdBy, items } = formData;
    setCanSubmit(
      deliveryDate &&
        packageNumber &&
        vendorName &&
        location &&
        owner &&
        createdBy &&
        items.length > 0
    );
  }, [formData]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleDateChange = (newDate) =>
    setFormData({ ...formData, deliveryDate: newDate });

  const handleAddItem = (e) => {
    // Prevent form submission if called from Enter key press
    if (e) {
      e.preventDefault();
    }
    const item = formData.newItem.trim();
    if (item) {
      setFormData({
        ...formData,
        items: [...formData.items, item],
        newItem: "",
      });
      // Clear items error when user adds an item
      if (fieldErrors.items) {
        setFieldErrors({ ...fieldErrors, items: false });
      }
    }
  };

  const handleDeleteItem = (idx) => {
    const items = [...formData.items];
    items.splice(idx, 1);
    setFormData({ ...formData, items });
  };

  const validateForm = () => {
    const errors = {};
    const missingFields = [];

    if (!formData.packageNumber.trim()) {
      errors.packageNumber = true;
      missingFields.push('Package Number');
    }
    if (!formData.vendorName.trim()) {
      errors.vendorName = true;
      missingFields.push('Vendor Name');
    }
    if (!formData.location.trim()) {
      errors.location = true;
      missingFields.push('Location');
    }
    if (!formData.owner.trim()) {
      errors.owner = true;
      missingFields.push('Owner');
    }
    if (!formData.deliveryDate) {
      errors.deliveryDate = true;
      missingFields.push('Delivery Date');
    }
    if (formData.items.length === 0) {
      errors.items = true;
      missingFields.push('at least one Item');
    }

    setFieldErrors(errors);

    if (missingFields.length > 0) {
      const message = `Please fill in: ${missingFields.join(', ')}`;
      setValidationError({ open: true, message });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate form even if button should be disabled
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      delivery_date: formData.deliveryDate,
      items: formData.items,
      package_number: formData.packageNumber,
      vendor_name: formData.vendorName,
      location: formData.location,
      owner: formData.owner,
      qr_created_by: formData.createdBy,
      qr_created_at: new Date().toISOString(),
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/submit`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.uuid) {
        // Generate the correct QR URL using current domain
        const currentDomain = window.location.origin;
        const qrUrl = `${currentDomain}/hindalco?uuid=${result.uuid}`;
        setQrData(qrUrl);
      } else if (result.url) {
        setQrData(result.url);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadQRCodeImage = async () => {
    const qrElement = document.getElementById("branded-qr-container");
    if (!qrElement) return;

    try {
      window.scrollTo(0, 0); // Scroll to top before capturing
      
      // Add a small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(qrElement, {
        scale: 3, // Higher scale for crisp QR codes
        useCORS: true, // Enable cross-origin images
        allowTaint: true, // Allow tainted canvases for better compatibility
        backgroundColor: '#ffffff', // Ensure white background
        logging: false, // Disable logging for cleaner output
        height: qrElement.offsetHeight,
        width: qrElement.offsetWidth,
        scrollX: 0,
        scrollY: 0,
        pixelRatio: window.devicePixelRatio || 1 // Use device pixel ratio for better quality
      });
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas has zero dimensions");
      }
      
      // Use maximum quality for PNG export
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      if (!imgData || imgData === 'data:,') {
        throw new Error("Failed to generate image data");
      }
      
      const link = document.createElement('a');
      link.href = imgData;
      const fileName = `QR_Code_${formData.packageNumber || 'Package'}_${new Date().toISOString().split('T')[0]}.png`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error during image download:", error);
      alert("Failed to download image. Please try again.");
    }
  };

  const downloadQRCodePDF = async () => {
    const qrElement = document.getElementById("branded-qr-container");
    if (!qrElement) return;

    try {
      window.scrollTo(0, 0); // Scroll to top before capturing
      
      // Add a small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(qrElement, {
        scale: 3, // Higher scale for crisp QR codes
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: qrElement.offsetHeight,
        width: qrElement.offsetWidth,
        scrollX: 0,
        scrollY: 0,
        pixelRatio: window.devicePixelRatio || 1
      });
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas has zero dimensions");
      }
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      if (!imgData || imgData === 'data:,') {
        throw new Error("Failed to generate image data for PDF");
      }

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add Hindalco branding header
      pdf.setFontSize(20);
      pdf.setTextColor(38, 58, 127); // Updated Hindalco blue
      pdf.setFont("helvetica", "bold");
      pdf.text("Hindalco Industries", pageWidth / 2, 30, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setTextColor(45, 55, 72); // Dark grey
      pdf.setFont("helvetica", "normal");
      pdf.text("Package Management System", pageWidth / 2, 45, { align: 'center' });

      // Add a line separator
      pdf.setDrawColor(226, 232, 240); // Light grey
      pdf.setLineWidth(1);
      pdf.line(20, 55, pageWidth - 20, 55);

      // Calculate QR code dimensions to fit properly
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const maxQrSize = Math.min(pageWidth - 60, pageHeight - 140);
      
      // Maintain aspect ratio
      const aspectRatio = imgWidth / imgHeight;
      let qrWidth = maxQrSize;
      let qrHeight = maxQrSize;
      
      if (aspectRatio > 1) {
        qrHeight = qrWidth / aspectRatio;
      } else {
        qrWidth = qrHeight * aspectRatio;
      }
      
      const qrX = (pageWidth - qrWidth) / 2;
      const qrY = 70;

      pdf.addImage(imgData, 'PNG', qrX, qrY, qrWidth, qrHeight);

      // Add scan instruction
      pdf.setFontSize(10);
      pdf.setTextColor(45, 55, 72);
      pdf.text("Scan with your device camera", pageWidth / 2, qrY + qrHeight + 15, { align: 'center' });

      // Add package info
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      const packageInfo = `Package: ${formData.packageNumber || 'N/A'} | Generated: ${new Date().toLocaleDateString()}`;
      pdf.text(packageInfo, pageWidth / 2, pageHeight - 40, { align: 'center' });
      
      pdf.setFontSize(8);
      pdf.text("Powered by Hindalco QR Management", pageWidth / 2, pageHeight - 25, { align: 'center' });

      const fileName = `QR_Code_${formData.packageNumber || 'Package'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error during PDF download:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({
      deliveryDate: "",
      packageNumber: "",
      vendorName: "",
      location: "",
      owner: "",
      createdBy: userEmail,
      newItem: "",
      items: [],
    });
    setQrData(null);
    setIsSubmitting(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleAdminDashboard = () => {
    navigate('/admin-dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppBar position="static" color="primary" elevation={0} sx={{ bgcolor: '#1F425C' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2, color: '#fff' }}
          >
            <ArrowBack />
          </IconButton>
          <img src="/HINDALCO.NS_BIG.png" alt="Hindalco" style={{ marginRight: '16px', height: '32px', width: 'auto' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff', fontWeight: 500 }}>
            Hindalco QR Management
          </Typography>
          {isAdmin && (
            <Button
              color="inherit"
              startIcon={<Dashboard />}
              onClick={handleAdminDashboard}
              sx={{
                ml: 2,
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Admin Dashboard
            </Button>
          )}
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ 
              ml: 2, 
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 12, pb: 4 }}>
        <Grid container spacing={3} justifyContent="center">
          {/* Form Card */}
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#1F425C',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Create New Package
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b',
                  }}
                >
                  Fill in the package details to generate a QR code
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Package Number"
                      value={formData.packageNumber}
                      onChange={(e) => {
                        setFormData({ ...formData, packageNumber: e.target.value });
                        // Clear error when user starts typing
                        if (fieldErrors.packageNumber) {
                          setFieldErrors({ ...fieldErrors, packageNumber: false });
                        }
                      }}
                      required
                      disabled={!!qrData}
                      error={fieldErrors.packageNumber}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: fieldErrors.packageNumber ? '#AC1E15' : '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: fieldErrors.packageNumber ? '#AC1E15' : '#1F425C',
                            borderWidth: 2,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: '#AC1E15',
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Vendor Name"
                      value={formData.vendorName}
                      onChange={(e) => {
                        setFormData({ ...formData, vendorName: e.target.value });
                        // Clear error when user starts typing
                        if (fieldErrors.vendorName) {
                          setFieldErrors({ ...fieldErrors, vendorName: false });
                        }
                      }}
                      required
                      disabled={!!qrData}
                      error={fieldErrors.vendorName}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: fieldErrors.vendorName ? '#AC1E15' : '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: fieldErrors.vendorName ? '#AC1E15' : '#1F425C',
                            borderWidth: 2,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: '#AC1E15',
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={(e) => {
                        setFormData({ ...formData, location: e.target.value });
                        // Clear error when user starts typing
                        if (fieldErrors.location) {
                          setFieldErrors({ ...fieldErrors, location: false });
                        }
                      }}
                      required
                      disabled={!!qrData}
                      error={fieldErrors.location}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: fieldErrors.location ? '#AC1E15' : '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: fieldErrors.location ? '#AC1E15' : '#1F425C',
                            borderWidth: 2,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: '#AC1E15',
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Owner"
                      value={formData.owner}
                      onChange={(e) => {
                        setFormData({ ...formData, owner: e.target.value });
                        // Clear error when user starts typing
                        if (fieldErrors.owner) {
                          setFieldErrors({ ...fieldErrors, owner: false });
                        }
                      }}
                      required
                      disabled={!!qrData}
                      error={fieldErrors.owner}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: fieldErrors.owner ? '#AC1E15' : '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: fieldErrors.owner ? '#AC1E15' : '#1F425C',
                            borderWidth: 2,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: '#AC1E15',
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Delivery Date"
                      type="date"
                      value={formData.deliveryDate}
                      onChange={(e) => {
                        setFormData({ ...formData, deliveryDate: e.target.value });
                        // Clear error when user selects date
                        if (fieldErrors.deliveryDate) {
                          setFieldErrors({ ...fieldErrors, deliveryDate: false });
                        }
                      }}
                      required
                      disabled={!!qrData}
                      error={fieldErrors.deliveryDate}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: fieldErrors.deliveryDate ? '#AC1E15' : '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: fieldErrors.deliveryDate ? '#AC1E15' : '#1F425C',
                            borderWidth: 2,
                          },
                          '&.Mui-error fieldset': {
                            borderColor: '#AC1E15',
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Created By"
                      value={userEmail}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          backgroundColor: '#fff !important',
                          '&:hover fieldset': {
                            borderColor: '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1F425C',
                            borderWidth: 2,
                          },
                          '& fieldset': {
                            borderRadius: '16px',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#fff !important',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23) !important',
                            },
                            '& .MuiInputBase-input': {
                              WebkitTextFillColor: '#000 !important',
                              color: '#000 !important',
                            },
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: fieldErrors.items ? '#AC1E15' : '#1F425C',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Items {fieldErrors.items && <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>- Please add at least one item</span>}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {formData.items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#fff',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Item Name"
                        value={item}
                        onChange={(e) => {
                          const items = [...formData.items];
                          items[index] = e.target.value;
                          setFormData({ ...formData, items });
                        }}
                        required
                        disabled={!!qrData}
                        InputProps={{
                          style: {
                            backgroundColor: '#fff !important',
                            color: '#000 !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#fff',
                            '&:hover fieldset': {
                              borderColor: '#1F425C',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1F425C',
                              borderWidth: 2,
                            },
                          },
                          '& .MuiInputBase-input': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                            color: '#000 !important',
                            '&:hover': {
                              backgroundColor: '#fff !important',
                              WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                              WebkitTextFillColor: '#000 !important',
                            },
                            '&:active': {
                              backgroundColor: '#fff !important',
                              WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                              WebkitTextFillColor: '#000 !important',
                            },
                            '&:focus': {
                              backgroundColor: '#fff !important',
                              WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                              WebkitTextFillColor: '#000 !important',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                              color: '#1F425C',
                            },
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => handleDeleteItem(index)}
                        disabled={!!qrData}
                        sx={{
                          color: '#e74c3c',
                          '&:hover': {
                            bgcolor: 'rgba(231, 76, 60, 0.1)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="New Item"
                      value={formData.newItem}
                      onChange={(e) => setFormData({ ...formData, newItem: e.target.value })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddItem(e);
                        }
                      }}
                      disabled={!!qrData}
                      InputProps={{
                        style: {
                          backgroundColor: '#fff !important',
                          color: '#000 !important',
                          WebkitTextFillColor: '#000 !important',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fff',
                          '&:hover fieldset': {
                            borderColor: '#1F425C',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1F425C',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: '#fff !important',
                          WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                          WebkitTextFillColor: '#000 !important',
                          color: '#000 !important',
                          '&:hover': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:active': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                          '&:focus': {
                            backgroundColor: '#fff !important',
                            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                            WebkitTextFillColor: '#000 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#1F425C',
                          },
                        },
                      }}
                    />
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddItem}
                      disabled={!!qrData}
                                          sx={{
                      color: '#AC1E15',
                      borderColor: '#AC1E15',
                      '&:hover': {
                        borderColor: '#8B1612',
                        bgcolor: 'rgba(230, 36, 38, 0.05)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'transform 0.2s ease-in-out',
                    }}
                      variant="outlined"
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting || !!qrData}
                  sx={{
                    py: 1.8,
                    borderRadius: 2,
                    bgcolor: '#1F425C',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(38, 58, 127, 0.2)',
                    '&:hover': {
                      bgcolor: '#1F2F6B',
                      boxShadow: '0 6px 20px rgba(38, 58, 127, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:disabled': {
                      bgcolor: '#bdc3c7',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    'Generate QR Code'
                  )}
                </Button>

                {qrData && (
                  <Box sx={{ 
                    mt: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center' 
                  }}>
                    <Box 
                      id="branded-qr-container" 
                      sx={{ 
                        position: 'relative', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}
                    >
                      <BrandedQRCode
                        value={qrData}
                        size={200}
                        title="Hindalco Industries"
                        subtitle="Package Management System"
                        variant="standard"
                        logoUrl="/HINDALCO.NS_BIG.png"
                        logoSize={0.3}
                      />
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
                        onClick={downloadQRCodeImage}
                        variant="contained"
                        sx={{
                          bgcolor: '#AC1E15',
                          '&:hover': {
                            bgcolor: '#8B1612',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      >
                        Download QR Code (PNG)
                      </Button>
                      <Button
                        onClick={downloadQRCodePDF}
                        variant="contained"
                        sx={{
                          bgcolor: '#1F425C',
                          '&:hover': {
                            bgcolor: '#1F2F6B',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      >
                        Download QR Code (PDF)
                      </Button>
                      <Button
                        onClick={resetForm}
                        variant="outlined"
                        sx={{
                          borderColor: '#AC1E15',
                          color: '#AC1E15',
                          '&:hover': {
                            borderColor: '#8B1612',
                            bgcolor: 'rgba(230, 36, 38, 0.05)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      >
                        Generate New
                      </Button>
                    </Box>
                  </Box>
                )}
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={validationError.open}
        autoHideDuration={6000}
        onClose={() => setValidationError({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setValidationError({ open: false, message: '' })} 
          severity="warning"
          sx={{ 
            width: '100%',
            bgcolor: '#FFF3CD',
            color: '#856404',
            '& .MuiAlert-icon': {
              color: '#F57C00'
            }
          }}
        >
          {validationError.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormPage;